import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, ShieldCheckIcon, ClockIcon, ZapIcon, RefreshCwIcon, LifeBuoyIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function MaintenanceService() {
  const features = [{
    icon: ShieldCheckIcon,
    title: 'Security Updates',
    description: 'Regular security patches and vulnerability fixes'
  }, {
    icon: RefreshCwIcon,
    title: 'Software Updates',
    description: 'Keep your CMS, plugins, and themes up to date'
  }, {
    icon: ZapIcon,
    title: 'Performance Optimisation',
    description: 'Speed improvements and technical enhancements'
  }, {
    icon: LifeBuoyIcon,
    title: 'Priority Support',
    description: 'Fast response times when you need help'
  }, {
    icon: ClockIcon,
    title: 'Regular Backups',
    description: 'Automated daily backups for peace of mind'
  }, {
    icon: ShieldCheckIcon,
    title: 'Uptime Monitoring',
    description: 'Round-the-clock monitoring to catch issues early'
  }];
  const benefits = ['Prevent security breaches and hacks', 'Keep your site running fast and smooth', 'Avoid costly downtime and lost revenue', 'Focus on your business, not tech issues', 'Stay compliant with latest standards', 'Regular performance improvements'];
  const plans = [{
    name: 'Essential',
    price: '£129/month',
    features: ['Security monitoring', 'Backups', 'Uptime monitoring', 'Plugin and theme updates', 'Yearly hosting, email and domain costs remain payable by the client']
  }, {
    name: 'Growth',
    price: '£299/month',
    features: ['Everything in Essential', 'All yearly technical costs covered in full', 'Content updates', 'Minor changes within a monthly allowance', 'Technical support', 'Monthly report'],
    popular: true
  }, {
    name: 'Performance',
    price: '£449/month',
    features: ['Everything in Growth', 'Monthly SEO improvements', 'Google Business Profile posts', 'Quarterly conversion review']
  }];
  return <>
      <Helmet data-id="element-2201">
        <title data-id="element-2202">Website Maintenance | Bright Pixxel Studio</title>
        <meta name="description" content="Keep your website secure, updated and fast with ongoing maintenance. Backups, updates, monitoring and support for UK businesses. See care plans." data-id="element-2203" />
        <link rel="canonical" href="https://bright-pixxel.com/services/maintenance" data-id="element-2204" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-2205" />
        <meta property="og:type" content="website" data-id="element-2206" />
        <meta property="og:title" content="Website Maintenance | Bright Pixxel Studio" data-id="element-2207" />
        <meta property="og:description" content="Keep your website secure, updated and fast with ongoing maintenance. Backups, updates, monitoring and support for UK businesses. See care plans." data-id="element-2208" />
        <meta property="og:url" content="https://bright-pixxel.com/services/maintenance" data-id="element-2209" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-2210" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2211" />
        <meta property="og:image:width" content="1200" data-id="element-2212" />
        <meta property="og:image:height" content="630" data-id="element-2213" />
        <meta property="og:image:type" content="image/png" data-id="element-2214" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - Website Maintenance" data-id="element-2215" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-2216" />
        <meta name="twitter:title" content="Website Maintenance | Bright Pixxel Studio" data-id="element-2217" />
        <meta name="twitter:description" content="Keep your website secure, updated and fast with ongoing maintenance. Backups, updates, monitoring and support for UK businesses. See care plans." data-id="element-2218" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2219" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - Website Maintenance" data-id="element-2220" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2221">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2222">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2223">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2224">
              <ShieldCheckIcon size={40} className="text-[#0DC3DC]" data-id="element-2225" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2226">
              UK Website Maintenance Services
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2227">
              Keep your website secure, fast, and up-to-date with our
              professional maintenance services. We handle the technical details
              so you can focus on your business.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2228">
              <Link to="/website-care" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-2229">
                View Care Plans
              </Link>
              <Link to="/contact" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-2230">
                Get Started
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2231">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2232">
          <div className="max-w-6xl mx-auto" data-id="element-2233">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-2234">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2235">
                Complete Website Care
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2236">
                Everything you need to keep your website running smoothly
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2237">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-2238">
                  <div className="w-12 h-12 bg-[#0DC3DC]/30 rounded-lg flex items-center justify-center mb-4 border border-[#0DC3DC]/40" data-id="element-2239">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-2240" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-2241">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-2242">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2243">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2244">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2245">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2246">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2247">
                Why Regular Maintenance Matters
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2248">
                Your website is a critical business asset. Regular maintenance
                protects your investment and ensures it continues to perform at
                its best.
              </p>
              <div className="space-y-4" data-id="element-2249">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-2250">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-2251">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-2252" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2253">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-2254">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Website performance monitoring dashboard - Bright Pixxel Studio professional website maintenance services" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-2255" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Plans Comparison */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2256">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2257">
          <div className="max-w-5xl mx-auto" data-id="element-2258">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2259">
              Choose Your Maintenance Package
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2260">
              {plans.map((plan, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className={`bg-[#F9FAFB] dark:bg-[#212C34] p-8 rounded-xl ${plan.popular ? 'ring-2 ring-[#0DC3DC] shadow-xl' : 'shadow-md'}`} data-id="element-2261">
                  {plan.popular && <div className="text-center mb-4" data-id="element-2262">
                      <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-sm font-medium" data-id="element-2263">
                        Most Popular
                      </span>
                    </div>}
                  <h3 className="text-2xl font-bold mb-2" data-id="element-2264">{plan.name}</h3>
                  <div className="text-3xl font-bold text-[#0DC3DC] mb-6" data-id="element-2265">
                    {plan.price}
                  </div>
                  <ul className="space-y-3" data-id="element-2266">
                    {plan.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-3" data-id="element-2267">
                        <CheckIcon size={18} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-2268" />
                        <span className="text-gray-700 dark:text-gray-300" data-id="element-2269">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                  <Link to="/website-care" className={`mt-8 block text-center px-6 py-3 rounded-lg font-medium transition-colors ${plan.popular ? 'bg-[#0DC3DC] text-white hover:bg-[#0DC3DC]/90' : 'bg-white dark:bg-[#1E1E1E] border-2 border-[#0DC3DC] text-[#0DC3DC] hover:bg-[#0DC3DC] hover:text-white'}`} data-id="element-2270">
                    View details
                  </Link>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2271">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2272">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2273">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2274">
              Protect Your Website Investment
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2275">
              Get started with our maintenance services today and enjoy peace of
              mind knowing your website is in expert hands.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2276">
              <a href="/contact" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-[#0C1B26] px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-lg" data-id="element-2277">
                Get your free homepage design
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}